import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DecimalPipe } from '@angular/common';
import { CustomValidators } from 'ng2-validation';
import { UrlConfig } from 'config/urlConfig';
import { NetworkService } from 'services/network.service';
import { DashboardService } from '../../../../../../services/dashboard.service';
import { EmailService } from '../../../../../../services/email.service';
import { DashboardDefaults } from '../../../../dashboard/dashboardDefaults';
import { SpinnerService } from 'services/spinner.service';
import { environment } from '../../../../../../../environments/environment';
import { SalesForceService } from 'services/salesforce.service';

import { Constants } from 'config/constants';

@Component({
    selector: 'tpc-thankyou',
    templateUrl: './thankyou.component.html',
    styleUrls: ['./thankyou.component.css']
})
export class ThankyouComponent implements OnInit {
    private isFormSubmitted = false;
    public calculatorName = '';
    public individualCalculatorName = Constants.calculators.individual;
    public isIndividual = false;
    public isSent = false;
    private _form: FormGroup;

    constructor(private fb: FormBuilder,
                private router: Router,
                private emailService: EmailService,
                private dashboardService: DashboardService,
                private numberPipe: DecimalPipe,
                private networkService: NetworkService,
                private spinner: SpinnerService,
                private salesForceService: SalesForceService) {
        this.getCurrentCalculatorName(router.url);
        this.isIndividual = this.calculatorName === this.individualCalculatorName;
        this.createForm();
    }

    ngOnInit() {
    }

    private getCurrentCalculatorName(currentUrl): void {
        let urlArray = currentUrl.split('/');
        this.calculatorName = urlArray.splice(1, 1)[0]; // url contains "", calculatorName, categoryName = so we take 1 in array
    }

    get form(): FormGroup {
        return this._form;
    }

    private createForm() {
        this._form = this.fb.group(this.prepareDataModel());
    }

    private prepareDataModel(): any {
        let model = {};
        if (this.isIndividual) {
            model = {
                email: ['', [
                    Validators.required,
                    Validators.minLength(5),
                    Validators.maxLength(50),
                    CustomValidators.email]],
                name: ['', [
                    Validators.minLength(2),
                    Validators.maxLength(50),
                    Validators.pattern(/^[A-Za-z0-9\-\s]+$/i)]],
                partner_channel: [''],
                marketing: true
            };
        } else {
            model = {
                email: ['', [
                    Validators.required,
                    Validators.minLength(5),
                    Validators.maxLength(50),
                    CustomValidators.email]]
            };
        }
        return model;
    }

    public checkError(param) {
        return this.form.get(param).status === 'INVALID' && (this.isFormSubmitted || this.form.get(param).touched);
    }

    public send(): void {
        let params;

        this.isFormSubmitted = true;

        if (this.form.status === 'VALID') {
            params = {
                email: this.getFV('email')
            };
            this.spinner.show();

            // check count of emails from this user
            this.networkService.post(UrlConfig.getUrl('checkEmail'), params).subscribe(
                response => {
                    let numberOfAttempts = response[0].Attempts;

                    if (numberOfAttempts > Constants.limitPerOneEmailPerDay) {
                        this.router.navigate(['/limit']);
                        this.spinner.hide();
                        return;
                    }

                    if (this.isIndividual) {
                        params.marketing = this.getFV('marketing');
                        params.partner_channel = this.getFV('partner_channel');
                        params.name = this.getFV('name');
                    }

                    switch (this.calculatorName) {
                        case 'business':
                            this.sendBusinessEmail(params);
                            break;
                        case 'individual':
                            this.sendIndividualEmail(params);
                            break;
                        case 'events':
                            this.sendEventsEmail(params);
                            break;
                    }
                }
            );
        }

    }

    private sendBusinessEmail(params) {
        let emissions;

        emissions = this.dashboardService.getCurrentDashboardModel();

        params.cart_url = environment.terrapassURL;
        params.trees = this.roundNumber(emissions.totals.trees);
        params.co2e_total_integer = this.formatEmissionToMetricTonnes(emissions.totalCategory.totalEmissions);
        params.co2e_total = this.roundNumber(this.formatEmissionToMetricTonnes(emissions.totalCategory.totalEmissions));
        params['co2e_site'] = this.roundNumber(this.formatEmissionToMetricTonnes(emissions.categories.site.totalEmissions));
        params['co2e_fleet'] = this.roundNumber(this.formatEmissionToMetricTonnes(emissions.categories.fleet.totalEmissions));
        params['co2e_travel'] = this.roundNumber(this.formatEmissionToMetricTonnes(emissions.categories.travel.totalEmissions));
        params['co2e_commute'] = this.roundNumber(this.formatEmissionToMetricTonnes(emissions.categories.commute.totalEmissions));
        params['co2e_shipping'] = this.roundNumber(this.formatEmissionToMetricTonnes(emissions.categories.shipping.totalEmissions));
        params['co2e_server'] = this.roundNumber(this.formatEmissionToMetricTonnes(emissions.categories.server.totalEmissions));

        this.emailService.sendBusinessEmissionsEmail(params, data => {
            this.isSent = true;
        });
    }

    private sendEventsEmail(params) {
        let emissions;

        params.cart_url = environment.terrapassURL;
        emissions = this.dashboardService.getCurrentDashboardModel();
        params.trees = this.roundNumber(emissions.totals.trees);
        params.co2e_total_integer = this.formatEmissionToMetricTonnes(emissions.totalCategory.totalEmissions);
        params.co2e_total = this.roundNumber(this.formatEmissionToMetricTonnes(emissions.totalCategory.totalEmissions));
        params['co2e_travel'] = this.roundNumber(this.formatEmissionToMetricTonnes(emissions.categories.travel.totalEmissions));
        params['co2e_venue'] = this.roundNumber(this.formatEmissionToMetricTonnes(emissions.categories.venue.totalEmissions));
        params['co2e_water'] = this.roundNumber(this.formatEmissionToMetricTonnes(emissions.categories.water.totalEmissions));
        params['co2e_meals'] = this.roundNumber(this.formatEmissionToMetricTonnes(emissions.categories.meals.totalEmissions));
        params['co2e_public_transit'] = this.roundNumber(this.formatEmissionToMetricTonnes(emissions.categories.commute.totalEmissions));

        this.emailService.sendEventsEmissionsEmail(params, data => {
            this.isSent = true;
        });
    }

    private sendIndividualEmail(params) {
        let emissions;

        emissions = this.dashboardService.getCurrentDashboardModel();

        params.cart_url = environment.terrapassURL;
        params.trees = this.roundNumber(emissions.totals.trees);
        params.co2e_total = this.formatEmissionToPounds(emissions.totalCategory.totalEmissions);
        params.round_total = this.roundNumber(params.co2e_total);

        params['co2e_vehicle'] = this.formatEmissionToPounds(emissions.categories.vehicle.totalEmissions);
        params['round_vehicle'] = this.roundNumber(params['co2e_vehicle']);

        params['co2e_transit'] = this.formatEmissionToPounds(emissions.categories.transit.totalEmissions);
        params['round_transit'] = this.roundNumber(params['co2e_transit']);

        params['co2e_travel'] = this.formatEmissionToPounds(emissions.categories.airTravel.totalEmissions);
        params['round_travel'] = this.roundNumber(params['co2e_travel']);

        params['co2e_home'] = this.formatEmissionToPounds(emissions.categories.home.totalEmissions);
        params['round_home'] = this.roundNumber(params['co2e_home']);

        params.co2e_average_total = this.formatEmissionToPounds(emissions.mtAvgTotal.usEmissionAverage);
        params['co2e_average_vehicle'] = this.formatEmissionToPounds(emissions.categories.vehicle.usEmissionAverage);
        params['co2e_average_transit'] = this.formatEmissionToPounds(emissions.categories.transit.usEmissionAverage);
        params['co2e_average_travel'] = this.formatEmissionToPounds(emissions.categories.airTravel.usEmissionAverage);
        params['co2e_average_home'] = this.formatEmissionToPounds(emissions.categories.home.usEmissionAverage);


        this.salesForceService.send(params, Constants.calculators.individual).subscribe(
            () => {
                this.emailService.sendIndividualEmissionsEmail(params, data => {
                    // this.spinner.hide();
                    this.isSent = true;
                });
            }
        );


    }

    private formatEmissionToPounds(num: number): number {
        return num * DashboardDefaults.config.POUNDS_MULTIPLIER;
    }

    private formatEmissionToMetricTonnes(num: number): number {
        return num * DashboardDefaults.config.TONNES_MULTIPLIER;
    }

    private roundNumber(num: number): string {
        return this.numberPipe.transform(num, '1.0-0');
    }

    getFV(param) {
        return this.form.get(param).value;
    }
}



// WEBPACK FOOTER //
// ./src/app/components/calculator/calculator-item/shared/main/thankyou/thankyou.component.ts